// Floating timer pill visible on every screen while timers exist; tap for controls.
// Remaining time is recomputed from the clock once a second (no background work needed).
import React, { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import Sheet from './Sheet';
import { Caption, IconButton } from './ui';
import { useTimers } from '../store/timers';
import { isDone, isRunning, remaining, sortTimers } from '../logic/timers';
import { formatClock } from '../logic/timerText';
import { firesInBackground, onAlarm } from '../platform/notifications';
import { notify } from './Dialogs';
import { font, radius, spacing, usePalette } from '../theme';

function useNow(active: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export function TimerList({ now }: { now: number }) {
  const p = usePalette();
  const { t } = useTranslation();
  const { timers, pause, resume, addMinute, cancel } = useTimers();
  return (
    <View style={{ gap: spacing.s }}>
      {sortTimers(timers, now).map(timer => {
        const done = isDone(timer, now);
        return (
          <View key={timer.id} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.s, backgroundColor: done ? p.tint : p.card, borderRadius: radius.l, padding: spacing.m }}>
            <Icon name="timer" color={done ? p.onTint : p.tint} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: font.body, fontWeight: '600', color: done ? p.onTint : p.text }} numberOfLines={1}>{timer.label}</Text>
              <Text style={{ fontSize: font.title2, fontWeight: '700', color: done ? p.onTint : p.text, fontVariant: ['tabular-nums'] }}>
                {done ? '0:00' : formatClock(remaining(timer, now) / 1000)}
              </Text>
            </View>
            <IconButton icon="plus" label={t('timers.plusMinute')} onPress={() => addMinute(timer.id)} color={done ? p.onTint : undefined} />
            {!done ? (
              <IconButton icon={isRunning(timer) ? 'pause' : 'play'} label={isRunning(timer) ? t('timers.pause') : t('timers.resume')} onPress={() => (isRunning(timer) ? pause(timer.id) : resume(timer.id))} />
            ) : null}
            <IconButton icon="close" label={t('timers.cancel')} onPress={() => cancel(timer.id)} color={done ? p.onTint : undefined} />
          </View>
        );
      })}
      {!firesInBackground ? <Caption>{t('timers.webNote')}</Caption> : null}
    </View>
  );
}

export default function TimerBar({ bottomOffset = 64 }: { bottomOffset?: number }) {
  const p = usePalette();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const timers = useTimers(s => s.timers);
  const [open, setOpen] = useState(false);
  const now = useNow(timers.length > 0);

  // Web/Telegram: ring in-app. Native: the OS notification does it.
  useEffect(() => onAlarm(title => notify(title)), []);

  if (timers.length === 0) return null;
  const next = sortTimers(timers, now)[0];
  const anyDone = timers.some(x => isDone(x, now));
  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('timers.title')}
        onPress={() => setOpen(true)}
        style={{
          position: 'absolute', right: spacing.l, bottom: insets.bottom + bottomOffset, flexDirection: 'row', alignItems: 'center', gap: spacing.s,
          backgroundColor: anyDone ? p.tint : p.elevated, borderRadius: radius.pill, paddingHorizontal: spacing.l, paddingVertical: spacing.s,
          borderWidth: 1, borderColor: p.border, // matte: an outline, no drop shadow
        }}>
        <Icon name="timer" color={anyDone ? p.onTint : p.tint} />
        <Text style={{ fontSize: font.headline, fontWeight: '700', color: anyDone ? p.onTint : p.text, fontVariant: ['tabular-nums'] }}>
          {isDone(next, now) ? next.label : formatClock(remaining(next, now) / 1000)}
        </Text>
        {timers.length > 1 ? <Text style={{ color: anyDone ? p.onTint : p.secondaryText }}>+{timers.length - 1}</Text> : null}
      </Pressable>
      <Sheet visible={open} onClose={() => setOpen(false)} title={t('timers.title')}>
        <TimerList now={now} />
      </Sheet>
    </>
  );
}
